import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '@/api';
import type { UserInfo, LoginRequest, ChangePasswordRequest } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserInfo | null>(null);
  const token = ref<string | null>(localStorage.getItem('access_token'));
  const loading = ref(false);
  const error = ref<string | null>(null);
  const requiresPasswordChange = computed(() => user.value?.is_password_change_required ?? false);

  const isAuthenticated = computed(() => !!token.value);

  const initAuth = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser);
      } catch {
        localStorage.removeItem('user');
      }
    }
  };

  const login = async (credentials: LoginRequest) => {
    loading.value = true;
    error.value = null;
    try {
      const response = await authApi.login(credentials);
      token.value = response.access_token;
      user.value = response.user;
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      return response;
    } catch (err: any) {
      error.value = err.response?.data?.detail || '登录失败';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
    } finally {
      token.value = null;
      user.value = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
  };

  const changePassword = async (data: ChangePasswordRequest) => {
    loading.value = true;
    error.value = null;
    try {
      await authApi.changePassword(data);
      if (user.value) {
        user.value.is_password_change_required = false;
        localStorage.setItem('user', JSON.stringify(user.value));
      }
    } catch (err: any) {
      error.value = err.response?.data?.detail || '修改密码失败';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchCurrentUser = async () => {
    try {
      user.value = await authApi.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(user.value));
    } catch {
      logout();
    }
  };

  return {
    user,
    token,
    loading,
    error,
    requiresPasswordChange,
    isAuthenticated,
    initAuth,
    login,
    logout,
    changePassword,
    fetchCurrentUser,
  };
});
